import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../components/ui/AppShell";
import { Button, Card, Chip, EmptyState, StatusPill } from "../components/ui";
import { ArrowLeft, Calendar, ExternalLink, Package } from "../components/icons";
import { getMyScorm, launchScorm } from "../services/ScormService";
import { formatDueDate, isOverdue, openLabel } from "../lib/learning";

/**
 * "My SCORM Courses" — packaged courses assigned to the learner.
 *
 * These also appear inside My Learning Plan; this page exists for the details
 * only SCORM has — the package version and the score its own tracking reported.
 * Launching opens the backend-hosted player in a new tab, which is what makes
 * the SCORM API discoverable to the package.
 */
export const MyScorm = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setItems(await getMyScorm());
      setLoading(false);
    })();
  }, []);

  return (
    <AppShell crumb="SCORM Courses" active="plan">
      <div className="ds-section__head" style={{ marginTop: 0 }}>
        <div>
          <h1 className="ds-h1">SCORM courses</h1>
          <p className="ds-sub">
            Packaged courses assigned to you. Each opens in its own player tab.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon={ArrowLeft}
          onClick={() => navigate("/my-learning")}
        >
          Back to plan
        </Button>
      </div>

      {loading && <p className="ds-sub">Loading your courses…</p>}

      {!loading && items.length === 0 && (
        <Card>
          <EmptyState
            icon={Package}
            text="No SCORM courses have been assigned to you yet."
          />
        </Card>
      )}

      {!loading && items.length > 0 && (
        <div className="ds-grid ds-grid--3">
          {items.map((item) => {
            const overdue = isOverdue(item);
            return (
              <Card key={item._id} accent pad className="ds-stack">
                <div className="ds-row-flex ds-between ds-gap-2">
                  <Chip>SCORM {item.scormVersion}</Chip>
                  <StatusPill status={item.status} overdue={overdue} />
                </div>

                <h3 className="ds-h3 ds-mt-3" style={{ lineHeight: 1.4 }}>
                  {item.title}
                </h3>
                {item.description && <p className="ds-sub">{item.description}</p>}

                <p
                  className="ds-sub ds-row-flex ds-gap-2"
                  style={{
                    marginTop: 8,
                    color: overdue ? "var(--danger-ink)" : undefined,
                    fontWeight: overdue ? 600 : undefined,
                  }}
                >
                  <Calendar size={14} />
                  {item.dueDate
                    ? `${overdue ? "Overdue — was due" : "Due"} ${formatDueDate(item.dueDate)}`
                    : "No due date"}
                  {item.scoreRaw != null && ` · Score ${item.scoreRaw}`}
                </p>

                <div style={{ marginTop: "auto", paddingTop: "var(--s-4)" }}>
                  <Button
                    block
                    icon={ExternalLink}
                    onClick={() => launchScorm(item.scormId)}
                  >
                    {openLabel(item.status)}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </AppShell>
  );
};

export default MyScorm;
